import type { Vector2 } from 'three';

export type LabelPlacement = {
    /** Mutable — adjusted by the resolver */
    position: Vector2;
    halfWidth: number;
    halfHeight: number;
};

/**
 * Resolve overlaps between label bounding boxes using iterative AABB repulsion.
 * Mutates each label's `position` in place.
 * Stops early when no overlaps remain in a pass.
 *
 * @param labels     Array of label placements to resolve
 * @param padding    Minimum gap between label edges
 * @param maxIterations Maximum number of passes (default 50)
 */
export function resolveLabelOverlaps(
    labels: LabelPlacement[],
    padding: number,
    maxIterations: number = 50,
): void {
    for (let iter = 0; iter < maxIterations; iter++) {
        let anyOverlap = false;

        for (let i = 0; i < labels.length; i++) {
            for (let j = i + 1; j < labels.length; j++) {
                const a = labels[i];
                const b = labels[j];
                const overlapX =
                    a.halfWidth + b.halfWidth + padding - Math.abs(a.position.x - b.position.x);
                const overlapY =
                    a.halfHeight + b.halfHeight + padding - Math.abs(a.position.y - b.position.y);

                if (overlapX > 0 && overlapY > 0) {
                    anyOverlap = true;
                    // Push apart along the axis of minimum overlap
                    if (overlapX < overlapY) {
                        const shift = overlapX / 2;
                        const sign = a.position.x <= b.position.x ? -1 : 1;
                        a.position.x += sign * shift;
                        b.position.x -= sign * shift;
                    } else {
                        const shift = overlapY / 2;
                        const sign = a.position.y <= b.position.y ? -1 : 1;
                        a.position.y += sign * shift;
                        b.position.y -= sign * shift;
                    }
                }
            }
        }

        if (!anyOverlap) break;
    }
}
